import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import {
  Copy,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  Trophy,
  AlertCircle,
  Download,
  Medal,
  Star,
  Zap,
  GraduationCap
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { checkAndAwardBadges, Badge } from '../../utils/badges';
import { updateStreak } from '../../utils/streak';
import { logActivity } from '../../utils/activity';
import { encodeId, findByEncodedId } from '../../utils/ids';
import {
  getUserPlanType,
  canAccessCategory,
  isPaidPlan,
  FREE_PLAN_LIMITS
} from '../../utils/subscription';
import { useTranslation } from 'react-i18next';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Flashcards() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [flashcards, setFlashcards] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [completedCards, setCompletedCards] = useState<Set<number>>(new Set());
  const [showCelebration, setShowCelebration] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const [newBadges, setNewBadges] = useState<Badge[]>([]);
  const [showBadgeModal, setShowBadgeModal] = useState(false);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        checkUser(),
        loadCategories()
      ]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (!loading && categoryId && categories.length > 0) {
      const category = findByEncodedId(categories, categoryId);
      if (category) {
        checkCategoryAccess(category.id);
      } else {
        window.REACT_APP_NAVIGATE('/pricing');
      }
    }
  }, [categoryId, subscription, categories, loading]);

  useEffect(() => {
    if (selectedCategory) {
      loadFlashcards(selectedCategory);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (!loading && !selectedCategory && categories.length > 0 && getUserPlanType(subscription) === 'free') {
      checkGlobalAccess();
    }
  }, [loading, selectedCategory, categories, subscription]);

  const checkGlobalAccess = async () => {
    if (!user) return;
    try {
      const { data: progress } = await supabase
        .from('user_progress')
        .select('flashcards_completed')
        .eq('user_id', user.id)
        .eq('category_id', categories[FREE_PLAN_LIMITS.allowedCategoryIndex].id)
        .maybeSingle();

      if (progress && progress.flashcards_completed >= FREE_PLAN_LIMITS.maxFlashcards) {
        window.REACT_APP_NAVIGATE('/pricing');
      }
    } catch (error) {
      console.error('Error checking global access:', error);
    }
  };

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        window.REACT_APP_NAVIGATE('/login');
        return;
      }

      setUser(user);
      await fetchSubscription(user.id);
    } catch (error) {
      console.error('Erreur:', error);
      window.REACT_APP_NAVIGATE('/login');
    }
  };

  const fetchSubscription = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error);
      }

      setSubscription(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const checkCategoryAccess = async (catId: number) => {
    if (categories.length === 0) return;

    const categoryIndex = categories.findIndex(c => c.id === catId);

    if (categoryIndex === -1 || !canAccessCategory(subscription, categoryIndex)) {
      window.REACT_APP_NAVIGATE('/pricing');
      return;
    }

    if (getUserPlanType(subscription) === 'free') {
      try {
        const { data: progress } = await supabase
          .from('user_progress')
          .select('flashcards_completed')
          .eq('user_id', user.id)
          .eq('category_id', catId)
          .maybeSingle();

        if (progress && progress.flashcards_completed >= FREE_PLAN_LIMITS.maxFlashcards) {
          window.REACT_APP_NAVIGATE('/pricing');
          return;
        }
      } catch (error) {
        console.error('Error checking progress:', error);
      }
    }

    setSelectedCategory(catId);
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('id', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const loadFlashcards = async (catId: number) => {
    try {
      const { data, error } = await supabase
        .from('flashcards')
        .select('*')
        .eq('category_id', catId)
        .order('id', { ascending: true });

      if (error) throw error;

      let shuffled = (data || []).sort(() => Math.random() - 0.5);

      const planType = getUserPlanType(subscription);
      if (planType === 'free') {
        shuffled = shuffled.slice(0, FREE_PLAN_LIMITS.maxFlashcards);
      }

      setFlashcards(shuffled);

      const { data: progress } = await supabase
        .from('user_progress')
        .select('flashcards_completed')
        .eq('user_id', user.id)
        .eq('category_id', catId)
        .maybeSingle();

      const resumeIndex = progress && progress.flashcards_completed < (data?.length || 0)
        ? progress.flashcards_completed
        : 0;

      setCurrentIndex(resumeIndex);
      setIsFlipped(false);
      setCompletedCards(new Set(data?.slice(0, resumeIndex).map((c: any) => c.id) || []));
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = async () => {
    if (!flashcards[currentIndex]) return;

    const newCompleted = new Set(completedCards);
    newCompleted.add(flashcards[currentIndex].id);
    setCompletedCards(newCompleted);

    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
      await updateProgress(currentIndex + 1);

      const category = categories.find(c => c.id === selectedCategory);
      logActivity({
        type: 'flashcards',
        title: (i18n.language === 'en' ? category?.title_en : category?.title) || category?.title || 'Flashcards',
        subtitle: t('flashcards.title_highlight'),
        value: `${currentIndex + 1}/${flashcards.length}`,
        categoryId: selectedCategory as number
      });
    } else {
      setShowCelebration(true);
      await updateProgress(flashcards.length);

      const category = categories.find(c => c.id === selectedCategory);
      logActivity({
        type: 'flashcards',
        title: (i18n.language === 'en' ? category?.title_en : category?.title) || category?.title || 'Flashcards',
        subtitle: t('flashcards.result.title'),
        value: `${flashcards.length} cards`,
        categoryId: selectedCategory as number
      });

      checkForBadges();
    }
  };

  const checkForBadges = async () => {
    if (!user || !selectedCategory) return;
    try {
      const { data: allProgress } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id);

      const currentProgress = allProgress?.find(p => p.category_id === selectedCategory);

      const quizCompleted = (currentProgress?.qcm_completed || 0) + (currentProgress?.vrai_faux_completed || 0);
      const progressPercent = currentProgress ? Math.round((quizCompleted / 100) * 100) : 0;

      let themesCompleted = 0;
      if (allProgress) {
        for (const p of allProgress) {
          if ((p.qcm_completed || 0) + (p.vrai_faux_completed || 0) >= 100) themesCompleted++;
        }
      }

      const awarded = await checkAndAwardBadges(user.id, selectedCategory, {
        score: 0,
        total: flashcards.length,
        progressPercent,
        totalThemesCompleted: themesCompleted
      });

      if (awarded.length > 0) {
        setNewBadges(awarded);
        setShowBadgeModal(true);
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const updateProgress = async (completedCount: number) => {
    if (!user || !selectedCategory) return;

    try {
      const { data: existing, error: fetchError } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('category_id', selectedCategory)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existing) {
        await supabase
          .from('user_progress')
          .update({
            flashcards_completed: Math.max(existing.flashcards_completed || 0, completedCount),
            last_activity: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('category_id', selectedCategory);
      } else {
        await supabase
          .from('user_progress')
          .insert({
            user_id: user.id,
            category_id: selectedCategory,
            flashcards_completed: completedCount,
            qcm_completed: 0,
            vrai_faux_completed: 0,
            total_score: 0,
            last_activity: new Date().toISOString()
          });
      }

      await updateStreak();
    } catch (error) {
      console.error('Erreur mise à jour:', error);
    }
  };

  const resetSession = () => {
    setShowCelebration(false);
    setCurrentIndex(0);
    setIsFlipped(false);
    setCompletedCards(new Set());
  };

  const handleDownload = async () => {
    if (!isPaidPlan(subscription)) {
      window.REACT_APP_NAVIGATE('/pricing');
      return;
    }
    if (!selectedCategory || flashcards.length === 0) return;

    const category = categories.find(c => c.id === selectedCategory);
    const categoryTitle = (i18n.language === 'en' ? category?.title_en : category?.title) || category?.title || 'Theme';
    const doc = new jsPDF();

    try {
      const logoUrl = '/logo/app-logo.png';
      doc.addImage(logoUrl, 'PNG', 165, 10, 25, 25);
    } catch (e) {
      console.error("Could not add logo to PDF", e);
    }

    doc.setFontSize(22);
    doc.setTextColor(220, 38, 38);
    const titleText = t('flashcards.pdf.title', { theme: categoryTitle });
    const titleLines = doc.splitTextToSize(titleText, 140);
    doc.text(titleLines, 20, 20);

    const titleHeight = titleLines.length * 8;
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(t('flashcards.pdf.footer'), 20, 20 + titleHeight);

    let y = 45;
    flashcards.forEach((card, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }

      const qText = (i18n.language === 'en' ? card.question_en : card.question) || card.question;
      const aText = (i18n.language === 'en' ? card.answer_en : card.answer) || card.answer;

      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55);
      doc.setFont("helvetica", "bold");
      const questionLines = doc.splitTextToSize(`${index + 1}. ${qText}`, 170);
      doc.text(questionLines, 20, y);
      y += (questionLines.length * 6) + 2;

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(75, 85, 99);
      const answerLines = doc.splitTextToSize(`${t('flashcards.pdf.label_answer')} : ${aText}`, 160);
      doc.text(answerLines, 25, y);
      y += (answerLines.length * 5) + 8;

      doc.setDrawColor(229, 231, 235);
      doc.line(20, y - 4, 190, y - 4);
    });

    doc.save(`Flashcards-${categoryTitle.split(' ')[0] || 'citoyenpro'}.pdf`);
  };

  const isPaid = isPaidPlan(subscription);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentCard = flashcards[currentIndex];
  const progressPercent = flashcards.length > 0 ? Math.round(((currentIndex + 1) / flashcards.length) * 100) : 0;

  return (
    <DashboardLayout user={user}>
      <SEO noindex={true} />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main mb-2 flex items-center gap-2">
            {t('flashcards.title')} <span className="text-primary">{t('flashcards.title_highlight')}</span>
          </h1>
          <p className="text-text-muted">{t('flashcards.subtitle')}</p>
        </div>

        {/* Category Selection */}
        {!selectedCategory && (
          <div className="grid md:grid-cols-3 gap-6">
            {categories.map((category, index) => {
              const isLocked = !canAccessCategory(subscription, index);
              const title = (i18n.language === 'en' ? category.title_en : category.title) || category.title;
              const description = (i18n.language === 'en' ? category.description_en : category.description) || category.description;

              return (
                <button
                  key={category.id}
                  onClick={() => isLocked ? window.REACT_APP_NAVIGATE('/pricing') : setSelectedCategory(category.id)}
                  className={`bg-dark-card rounded-2xl border border-dark-border overflow-hidden transition-all text-left group ${isLocked ? 'opacity-75' : 'hover:border-primary/50'}`}
                >
                  <div className="h-32 bg-dark-bg relative flex items-center justify-center border-b border-dark-border group-hover:bg-dark-hover transition-colors">
                    <span className="text-4xl font-bold text-primary opacity-20 group-hover:opacity-40 transition-opacity">
                      {index + 1}
                    </span>
                    {isLocked && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="w-12 h-12 bg-dark-card rounded-full flex items-center justify-center border border-dark-border">
                          <AlertCircle className="text-text-muted" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold text-text-main group-hover:text-primary transition-colors">{title}</h3>
                      {isLocked && (
                        <span className="px-2 py-0.5 bg-red-500/10 text-red-600 text-xs font-bold rounded border border-red-500/20">
                          PRO
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-muted">{description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Flashcard View */}
        {selectedCategory && !showCelebration && (
          <div>
            {flashcards.length === 0 ? (
              <div className="bg-dark-card rounded-2xl border border-dark-border p-12 text-center">
                <Copy className="mx-auto text-text-muted mb-4" size={48} />
                <h2 className="text-xl font-bold text-text-main mb-2">{t('flashcards.status.no_flashcards')}</h2>
                <p className="text-text-muted mb-6">{t('flashcards.status.no_flashcards_desc')}</p>
                <div className="flex gap-4 justify-center">
                  <button onClick={() => setSelectedCategory(null)} className="px-6 py-2 bg-dark-hover text-text-main rounded-xl">{t('flashcards.status.back')}</button>
                </div>
              </div>
            ) : (
              <>
                {/* Navigation and Progress */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className="flex items-center gap-2 text-text-muted hover:text-text-main transition-colors"
                    >
                      <ArrowLeft size={20} />
                      {t('flashcards.nav.back_themes')}
                    </button>
                    {isPaid && (
                      <button
                        onClick={handleDownload}
                        className="flex items-center gap-2 text-xs font-bold text-primary hover:text-white bg-primary/10 hover:bg-primary px-3 py-1.5 rounded-lg transition-all"
                      >
                        <Download size={14} />
                        {t('flashcards.nav.download_pdf')}
                      </button>
                    )}
                  </div>
                  <div className="text-sm font-bold text-primary">
                    {currentIndex + 1} / {flashcards.length}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full h-1 bg-dark-card rounded-full mb-8 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                {/* Flashcard Container */}
                {currentCard && (
                  <div className="mb-8 perspective-1000 min-h-[400px]">
                    <div
                      className="flashcard-container cursor-pointer group"
                      onClick={handleFlip}
                    >
                      <div className={`flashcard ${isFlipped ? 'flipped' : ''}`}>
                        {/* Front */}
                        <div className="flashcard-face flashcard-front">
                          <div className="bg-dark-card border border-dark-border rounded-3xl p-12 flex flex-col items-center justify-center h-full min-h-[400px] hover:border-primary/50 transition-colors shadow-2xl">
                            <div className="mb-6 text-primary opacity-50">{t('flashcards.card.question')}</div>
                            <h3 className="text-2xl md:text-3xl font-bold text-text-main text-center leading-relaxed">
                              {(i18n.language === 'en' ? currentCard.question_en : currentCard.question) || currentCard.question}
                            </h3>
                            <div className="mt-8 text-sm text-text-muted flex items-center gap-2">
                              <RotateCcw size={16} />
                              {t('flashcards.card.click_flip')}
                            </div>
                          </div>
                        </div>

                        {/* Back */}
                        <div className="flashcard-face flashcard-back">
                          <div className="bg-dark-card border border-primary/30 rounded-3xl p-12 flex flex-col items-center justify-center h-full min-h-[400px] shadow-2xl relative overflow-hidden">
                            <div className="absolute inset-0 bg-primary/5 pointer-events-none" />
                            <div className="mb-6 text-primary font-bold">{t('flashcards.card.answer')}</div>
                            <h3 className="text-xl md:text-2xl font-bold text-text-main text-center mb-6 leading-relaxed">
                              {(i18n.language === 'en' ? currentCard.answer_en : currentCard.answer) || currentCard.answer}
                            </h3>
                            {((i18n.language === 'en' ? currentCard.explication_en : currentCard.explication) || currentCard.explication) && (
                              <p className="text-text-muted text-center text-sm max-w-lg">
                                {(i18n.language === 'en' ? currentCard.explication_en : currentCard.explication) || currentCard.explication}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Controls */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    className="w-14 h-14 rounded-full bg-dark-card border border-dark-border flex items-center justify-center text-text-main hover:bg-dark-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <ArrowLeft size={24} />
                  </button>

                  <button
                    onClick={handleFlip}
                    className="px-8 py-3 bg-dark-card border border-dark-border text-text-main font-bold rounded-xl hover:bg-dark-hover transition-all"
                  >
                    {isFlipped ? t('flashcards.nav.flip_question') : t('flashcards.nav.flip_answer')}
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={!isFlipped}
                    className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <ArrowRight size={24} />
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Celebration */}
        {showCelebration && (
          <div className="bg-dark-card rounded-3xl border border-dark-border p-12 text-center max-w-lg mx-auto">
            <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trophy className="text-primary" size={48} />
            </div>

            <h2 className="text-3xl font-bold text-text-main mb-2">{t('flashcards.result.title')}</h2>
            <p className="text-text-muted mb-8">
              {t('flashcards.result.subtitle')}
            </p>

            <div className="flex flex-col gap-3">
              {getUserPlanType(subscription) === 'free' ? (
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
                  className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors flex flex-col items-center justify-center"
                >
                  <span className="text-lg">{t('flashcards.result.unlock_pro')}</span>
                  <span className="text-[10px] opacity-80 font-normal">{t('flashcards.result.unlock_pro_desc')}</span>
                </button>
              ) : (
                <button
                  onClick={resetSession}
                  className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
                >
                  <RotateCcw size={20} /> {t('flashcards.result.retry')}
                </button>
              )}
              <button
                onClick={() => setSelectedCategory(null)}
                className="w-full py-3 bg-dark-bg text-text-muted font-bold rounded-xl border border-dark-border hover:border-primary/50 hover:text-primary transition-colors"
              >
                {t('flashcards.result.other_theme')}
              </button>
            </div>
          </div>
        )}

        {/* Badge Award Modal */}
        {showBadgeModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-dark-card border border-primary/30 rounded-3xl p-8 max-w-md w-full text-center relative overflow-hidden group animate-in zoom-in duration-500">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
              <div className="relative z-10">
                <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                  <Medal className="text-primary animate-bounce-slow" size={48} />
                  <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping opacity-25" />
                </div>
                <h2 className="text-3xl font-black text-text-main mb-2">{t('flashcards.badges.new')}</h2>
                <p className="text-text-muted mb-8">{t('flashcards.badges.congrats')}</p>
                <div className="space-y-4 mb-8">
                  {newBadges.map(badge => (
                    <div key={badge.id} className="bg-dark-bg/50 border border-dark-border rounded-2xl p-4 flex items-center gap-4 text-left">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        {badge.icon === 'Trophy' && <Trophy size={24} />}
                        {badge.icon === 'Star' && <Star size={24} />}
                        {badge.icon === 'Zap' && <Zap size={24} />}
                        {badge.icon === 'GraduationCap' && <GraduationCap size={24} />}
                      </div>
                      <div>
                        <h4 className="font-bold text-text-main">
                          {(i18n.language === 'en' ? badge.title_en : badge.title) || badge.title}
                        </h4>
                        <p className="text-xs text-text-muted">
                          {(i18n.language === 'en' ? badge.description_en : badge.description) || badge.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowBadgeModal(false)}
                  className="w-full py-4 bg-primary hover:bg-primary-hover text-white font-black rounded-2xl transition-all shadow-lg shadow-primary/20 active:scale-95"
                >
                  {t('flashcards.badges.close')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
